import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { startWith, takeUntil } from 'rxjs/operators';
import { DateTimezoneService } from '@services/date-timezone.service';
import { Observable, Subject, fromEvent } from 'rxjs';
import { IDefaultSelect } from '@interfaces/default-select';
import { TranslateService } from '@ngx-translate/core';
import { enumDatesDropdownType } from '@shared/form-wrapper/enums/enumDatesDropdownType';
import { BsDatepickerDirective } from 'ngx-bootstrap/datepicker';
import dayjs from '@services/dayjs.service';

@Component({
  selector: 'date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
})
export class DateRangePickerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public wrapperFc: UntypedFormControl | AbstractControl;
  @Input() public fc: UntypedFormControl | AbstractControl;
  @Input() public errors: IErrorForm;
  @Input() public label: string;
  @Input() public datesSelectionContainerButtonText: string = 'BUTTON.SEARCH';
  @Input() public showHelper: boolean = false;
  @Input() public clearable: boolean = false;
  @Input() public searchable: boolean = false;
  @Input() public helper: string;
  @Input() public placeholder: string;
  @Input() public id: string | number;
  @Input() public name: string | number;
  @Input() public maxDate: string;
  @Input() public minDate: string;
  @Input() public errorsOverrideParams: any;
  @Input() public showCurrentDate: boolean = false;
  @Input() public required: boolean = false;
  @Input() public isShowDisabled: boolean = false;
  @Input() public labelClass: string;
  @Input() public isWithIcon: boolean = false;
  @Input() public isSearchOnDateRangeSelect: boolean = false;
  @Input() public defaultDatesDropdownValue: enumDatesDropdownType;
  @Input() public defaultDatesRangeValue: Date[];
  @Output() private search = new EventEmitter<Date[]>();
  @Output() private datesDropdownSelect = new EventEmitter<enumDatesDropdownType>();

  @ViewChild('inputElement') public inputElement: ElementRef;

  public enumDatesDropdownType: typeof enumDatesDropdownType = enumDatesDropdownType;
  public datesDropdownControl: UntypedFormControl;
  public datesItems: IDefaultSelect[] = [];
  public dateRangeValue: Date[] = [];
  public dateValue: string;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private bsdpCustomButtons$: Observable<Event>;
  private isFirstLoad: boolean = true;
  constructor(private dateTimezoneService: DateTimezoneService, private translateService: TranslateService) {}

  public ngOnInit(): void {
    this.datesDropdownControl = new UntypedFormControl(this.defaultDatesDropdownValue);
    this.dateRangeValue = this.defaultDatesRangeValue;
    this.datesItems = Object.keys(enumDatesDropdownType)
      .filter((item: string) => Number(item) > -1)
      .map((item: any) => {
        const enumItem: number = Number(item);
        let label = '';

        switch (enumItem) {
          case enumDatesDropdownType.THIS_MONTH:
            label = this.translateService.instant(`BUTTON.THIS_MONTH`);
            break;
          case enumDatesDropdownType.PREVIOUS_MONTH:
            label = this.translateService.instant(`BUTTON.PREVIOUS_MONTH`);
            break;
          case enumDatesDropdownType.THIS_YEAR:
            label = this.translateService.instant(`BUTTON.THIS_YEAR`);
            break;
          case enumDatesDropdownType.CHOOSE_DATE_RANGE:
            label = this.translateService.instant(`BUTTON.CHOOSE_DATE_RANGE`);
            break;
          default:
            break;
        }

        return {
          id: enumItem,
          label,
          value: enumItem,
        };
      });

    this.fc.valueChanges.pipe(startWith(this.fc.value), takeUntil(this.destroy$)).subscribe((value: string) => {
      if (!value) {
        // Reset options dropdown to default
        this.datesDropdownControl.patchValue(null, { emitEvent: false });
      }
      this.dateValue = this.dateTimezoneService.formatDateWithoutTime(value);
    });
  }

  ngAfterViewInit(): void {
    this.datesDropdownControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: enumDatesDropdownType) => {
      this.onDatesDropdownSelect(value);
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  public onDatesDropdownSelect(value: enumDatesDropdownType) {
    this.dateRangeValue = [];
    this.datesDropdownSelect.emit(value);
    if (value === enumDatesDropdownType.CHOOSE_DATE_RANGE) {
      this.inputElement.nativeElement.click();
      this.inputElement.nativeElement.focus();
      return;
    }

    const today = new Date().toISOString();
    switch (value) {
      case enumDatesDropdownType.THIS_MONTH:
        const startOfTheMonth = this.dateTimezoneService.startOfTheMonth(today);
        const endOfTheMonth = this.dateTimezoneService.endOfTheMonth(today);
        this.dateRangeValue.push(new Date(startOfTheMonth));
        this.dateRangeValue.push(new Date(endOfTheMonth));
        this.search.emit(this.dateRangeValue);
        break;
      case enumDatesDropdownType.PREVIOUS_MONTH:
        const startOfPreviousMonth = this.dateTimezoneService.startOfThePreviousMonth(today);
        const endOfPreviousMonth = this.dateTimezoneService.endOfThePreviousMonth(today);
        this.dateRangeValue.push(new Date(startOfPreviousMonth));
        this.dateRangeValue.push(new Date(endOfPreviousMonth));
        this.search.emit(this.dateRangeValue);
        break;
      case enumDatesDropdownType.THIS_YEAR:
        const startOfTheYear = this.dateTimezoneService.startOfTheYear(today);
        const endOfTheYear = this.dateTimezoneService.endOfTheYear(today);
        this.dateRangeValue.push(new Date(startOfTheYear));
        this.dateRangeValue.push(new Date(endOfTheYear));
        this.search.emit(this.dateRangeValue);
        break;
      default:
        break;
    }
  }

  public onShown(dp: BsDatepickerDirective): void {
    if (!this.isSearchOnDateRangeSelect) {
      this.createCustomBsdpButtons(dp);
    }
  }

  public onDatePickerChange(value: any, dp: BsDatepickerDirective) {
    const isValidForChange = this.isChooseDateRangeSelected && value.length === 2 && !this.isFirstLoad;

    if (this.isSearchOnDateRangeSelect && isValidForChange) {
      const startDate = new Date(this.toUtcSameLocalTime(value[0], 'start'));
      const endDate = new Date(this.toUtcSameLocalTime(value[1], 'end'));
      this.search.emit([startDate, endDate]);
      return;
    }

    //NOTE: Dirty solution due to ngx-bootstrap lib current limitation
    //keeping date picker open even after a date has been selected
    if (isValidForChange) {
      dp.bsConfig.isAnimated = false;
      setTimeout(() => {
        dp.show();
        dp.bsConfig.isAnimated = true;
      }, 0);
    } else {
      this.isFirstLoad = false;
    }
  }

  public get minDateFormatted(): Date {
    if (this.minDate) {
      return this.dateTimezoneService.formatModelValue(this.minDate);
    }
    return undefined;
  }

  public get maxDateFormatted(): Date {
    if (this.maxDate) {
      return this.dateTimezoneService.formatModelValue(this.maxDate);
    }
    return undefined;
  }

  public get isChooseDateRangeSelected(): boolean {
    return this.datesDropdownControl.value === enumDatesDropdownType.CHOOSE_DATE_RANGE;
  }

  private get bsdpDropdownCustomButtons(): string {
    let customButtonsHtml = `
      <div class="bs-datepicker-actions-container">
        <button id="bsdpSearchButton" class="bsdpCustomButton search-button btn btn-sm btn-square btn-primary">
          ${this.translateService.instant(this.datesSelectionContainerButtonText)}
        </button>
      </div>
    `;
    return customButtonsHtml;
  }

  private createCustomBsdpButtons(dp: BsDatepickerDirective) {
    var bsDatePickerContainer = document.querySelector('.bs-datepicker-container');

    bsDatePickerContainer.insertAdjacentHTML('beforeend', this.bsdpDropdownCustomButtons);

    const bsdpCustomButton = document.getElementsByClassName('bsdpCustomButton');
    this.bsdpCustomButtons$ = fromEvent(bsdpCustomButton, 'click');

    this.bsdpCustomButtons$.pipe(takeUntil(this.destroy$)).subscribe((e: any) => {
      this.handleCustomBsdpButtonClickEvent(e, dp);
    });
  }

  private handleCustomBsdpButtonClickEvent(e, dp: BsDatepickerDirective) {
    switch (e.target.id) {
      case 'bsdpSearchButton':
        if (!this.dateRangeValue) return;
        const startDate = new Date(this.toUtcSameLocalTime(this.dateRangeValue[0], 'start'));
        const endDate = new Date(this.toUtcSameLocalTime(this.dateRangeValue[1], 'end'));
        this.search.emit([startDate, endDate]);
        dp.hide();
        break;
      default:
        break;
    }
  }

  private toUtcSameLocalTime(date: Date, type: 'start' | 'end'): string {
    const d = dayjs(date);
    if (type === 'start') {
      return dayjs
        .utc({
          year: d.year(),
          month: d.month(),
          day: d.date(),
          hour: 0,
          minute: 0,
          second: 0,
        })
        .format();
    } else {
      return dayjs
        .utc({
          year: d.year(),
          month: d.month(),
          day: d.date(),
          hour: 23,
          minute: 59,
          second: 59,
        })
        .format();
    }
  }
}
